import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BiDotsHorizontalRounded, BiDownload, BiInfoCircle, BiShow, BiHide, BiCalendar, BiTable, BiX } from 'react-icons/bi';
import { CgMaximize } from 'react-icons/cg';
import { BsDatabase } from 'react-icons/bs';
import { Modal, Button } from 'react-bootstrap';
import './ChartMenu.scss';
import useHasPermission from '../../core/hooks/useHasPermission';

const ChartMenu = ({
  chartId,
  onGetInfo,
  onToggleLabels,
  onToggleView,
  onMaximize,
  onDataSources,
  onViewTable,
  onDownload,
  onDeactivate,
  showLabels = true,
  isMonthView = true,
  isMaximized = false,
  showTableView = false,
  chartInfo = null,
  chartType = null,
  // Props to control menu item visibility
  showGetInfo = true,
  showToggleLabels = true,
  showToggleView = true,
  showMaximize = true,
  showDataSources = false,
  showViewTable = true,
  showDownload = true,
  showDeactivate = true
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const dropdownRef = useRef(null);
  const { hasDashboardUpdatePermission } = useHasPermission();

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Handle escape key to close dropdown
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleMenuItemClick = (action) => {
    setIsOpen(false);
    switch (action) {
      case 'getInfo':
        setShowInfoModal(true);
        onGetInfo?.();
        break;
      case 'toggleLabels':
        onToggleLabels?.();
        break;
      case 'toggleView':
        onToggleView?.();
        break;
      case 'maximize':
        onMaximize?.();
        break;
      case 'dataSources':
        onDataSources?.();
        break;
      case 'viewTable':
        onViewTable?.();
        break;
      case 'download':
        onDownload?.();
        break;
      case 'deactivate':
        onDeactivate?.();
        break;
      default:
        break;
    }
  };

  const handleCloseInfoModal = () => {
    setShowInfoModal(false);
  };

  return (
    <>
      <div className="chart-menu" ref={dropdownRef}>
        <button
          className="chart-menu__toggle"
          onClick={handleToggle}
          aria-label={t("chartMenu.label")}
          type="button"
        >
          <BiDotsHorizontalRounded />
        </button>

        {isOpen && (
          <div className="chart-menu__dropdown">
            {showGetInfo && (
              <button
                onClick={() => handleMenuItemClick('getInfo')}
                className="chart-menu__item"
              >
                <BiInfoCircle className="chart-menu__icon" />
                {t("getInfo.label")}
              </button>
            )}

            {showToggleLabels && (
              <button
                onClick={() => handleMenuItemClick('toggleLabels')}
                className="chart-menu__item"
              >
                {showLabels ? (
                  <>
                    <BiHide className="chart-menu__icon" />
                    {t("hideLabels.label")}
                  </>
                ) : (
                  <>
                    <BiShow className="chart-menu__icon" />
                    {t("viewDataLabels.label")}
                  </>
                )}
              </button>
            )}

            {showToggleView && chartType !== 'PIE' && chartType !== 'KPI' && (
              <button
                onClick={() => handleMenuItemClick('toggleView')}
                className="chart-menu__item"
              >
                <BiCalendar className="chart-menu__icon" />
                {isMonthView ? t("yearView.label") : t("monthView.label")}
              </button>
            )}

            {showMaximize && (
              <button
                onClick={() => handleMenuItemClick('maximize')}
                className="chart-menu__item"
              >
                <CgMaximize className="chart-menu__icon" />
                {isMaximized ? t("minimize.label") : t("maximize.label")}
              </button>
            )}

            {showDataSources && (
              <button
                onClick={() => handleMenuItemClick('dataSources')}
                className="chart-menu__item"
              >
                <BsDatabase className="chart-menu__icon" />
                {t("dataSources.label")}
              </button>
            )}

            {showViewTable && (
              <button
                onClick={() => handleMenuItemClick('viewTable')}
                className="chart-menu__item"
              >
                <BiTable className="chart-menu__icon" />
                {showTableView ? t("hideTable.label") : t("viewTable.label")}
              </button>
            )}

            {showDownload && (
              <button
                onClick={() => handleMenuItemClick('download')}
                className="chart-menu__item"
              >
                <BiDownload className="chart-menu__icon" />
                {t("Download.label")}
              </button>
            )}

            {showDeactivate && (
              <button
                hidden={!hasDashboardUpdatePermission()}
                onClick={() => handleMenuItemClick('deactivate')}
                className="chart-menu__item chart-menu__item--danger"
              >
                <BiX className="chart-menu__icon" />
                {t("deactivate.label")}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Chart Info Modal */}
      <Modal
        className='NewDashboard_modal chart-info-modal'
        show={showInfoModal}
        onHide={handleCloseInfoModal}
        aria-labelledby="chart-info-modal-title 1"
        centered
      >
        {/* <Modal.Header closeButton>

        </Modal.Header> */}
        <Modal.Body>

          <div className="chart-info-content px-4 py-4">
            <div className="text-end">
              <button type="button" className="btn btn-sm btn-close border-0 ms-2" aria-label="Close"
                onClick={handleCloseInfoModal}></button>
            </div>
            {chartInfo ? (
              <div>
                {/* <div className="info-item">
                  <strong>{t("chartInfo.chartName.label")}:</strong> {chartInfo.name || t("chartInfo.noName.label")}
                </div>
                <div className="info-item">
                  <strong>{t("chartInfo.chartType.label")}:</strong> {chartInfo.type || t("chartInfo.noType.label")}
                </div> */}
                {chartInfo.description && (
                  <div>
                    <strong>{t("chartInfo.description.label")}:</strong>
                    <p className="description-text">{chartInfo.description}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="no-info-message">
                {t("chartInfo.noInfoAvailable.label")}
              </div>
            )}
          </div>
        </Modal.Body>
        {/* <Modal.Footer style={{ justifyContent: 'right' }}>
          <Button variant="secondary" onClick={handleCloseInfoModal}>
            {t("Close.label")}
          </Button>
        </Modal.Footer> */}
      </Modal>
    </>
  );
};

export default ChartMenu; 